import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo } from 'react';
import { useExecuteRequest } from '@core/context/useExecuteRequest';
import InfoCard from './InfoCard';
/**
 * Type guard to check if details is UserDetailsConfig (object with 'fields' property)
 */
function isUserDetailsConfig(details) {
    return details && typeof details === 'object' && !Array.isArray(details) && 'fields' in details;
}
/**
 * Type guard to check if details is an array of UserDetailField
 */
function isUserDetailFieldArray(details) {
    return Array.isArray(details);
}
/**
 * Converts a field name from camelCase to Title Case
 * Example: "firstName" -> "First Name", "emailAddress" -> "Email Address"
 */
function fieldNameToLabel(fieldName) {
    return fieldName
        // Insert space before capital letters
        .replace(/([A-Z])/g, ' $1')
        // Capitalize first letter
        .replace(/^./, (str) => str.toUpperCase())
        // Trim any extra spaces
        .trim();
}
/**
 * Builds ObjectField array from UserDetailField configuration
 */
function buildDetailFields(user, fieldConfig) {
    if (!user)
        return [];
    return fieldConfig.map((config) => {
        if (typeof config === 'string') {
            // Simple field name - auto-generate label
            return {
                label: fieldNameToLabel(config),
                value: user[config]
            };
        }
        else {
            // Field with custom config
            return {
                label: config.label || fieldNameToLabel(config.field),
                value: user[config.field],
                icon: config.icon
            };
        }
    });
}
const userDetailsCache = new Map();
/**
 * @export
 * A component that fetches and displays user information using InfoCard.
 * Automatically constructs profile image URL from user data.
 *
 * @example Basic usage (no dropdown):
 * ```
 * <UserInfoCard userKey={123} />
 * ```
 *
 * @example Simple details with auto-generated labels (array):
 * ```
 * <UserInfoCard
 *   userKey={123}
 *   details={['Phone', 'Email', 'UserType']}
 * />
 * ```
 *
 * @example Details with custom labels and icons (array with objects):
 * ```
 * <UserInfoCard
 *   userKey={123}
 *   details={[
 *     { field: 'Phone', label: 'Phone Number', icon: 'fas phone' },
 *     { field: 'Email', label: 'Email Address', icon: 'fas envelope' },
 *     'UserType'  // Auto-generated label: "User Type"
 *   ]}
 * />
 * ```
 *
 * @example Full config with layout options (config object):
 * ```
 * <UserInfoCard
 *   userKey={123}
 *   details={{
 *     fields: ['Phone', 'Email', 'OfficePhone', 'UserType'],
 *     columns: 2,
 *     layout: 'vertical'
 *   }}
 * />
 * ```
 *
 * @example Custom details rendering (function):
 * ```
 * <UserInfoCard
 *   userKey={123}
 *   details={(user) => (
 *     <div>
 *       <h3>{user.FullName}</h3>
 *       <p>{user.Email}</p>
 *     </div>
 *   )}
 * />
 * ```
 *
 * @example Avatar variant with simple details:
 * ```
 * <UserInfoCard
 *   userKey={123}
 *   variant="avatar"
 *   size="large"
 *   shape="circle"
 *   details={['Email', 'Phone']}
 * />
 * ```
 */
const UserInfoCard = (props) => {
    var _a;
    const { userKey, details, variant, size, shape, dropdownPosition = 'bottom-left', className, style } = props;
    // Fetch user data
    const { loading, data: user, error } = useExecuteRequest({
        type: 'service',
        app: 'User',
        service: 'User:Details',
        parameters: { UserKey: userKey },
        options: { json: true, enableCache: true, cacheTime: (24 * 60) },
        extractData: [0]
    }, {
        autoExecute: true,
        dependencies: [userKey]
    });
    const userKeyStr = String(userKey);
    useEffect(() => {
        if (user)
            userDetailsCache.set(userKeyStr, user);
    }, [user, userKeyStr]);
    const effectiveUser = (_a = user !== null && user !== void 0 ? user : userDetailsCache.get(userKeyStr)) !== null && _a !== void 0 ? _a : null;
    // Transform user data to include constructed profile image URL
    const transformedData = useMemo(() => {
        if (!effectiveUser)
            return null;
        const profileImageUrl = effectiveUser.ProfileImageName
            ? `/AccountResources/profilepics/${effectiveUser.ProfileImageName}`
            : '';
        return Object.assign(Object.assign({}, effectiveUser), { profileImageUrl });
    }, [effectiveUser]);
    // Build details prop for InfoCard based on the type of details provided
    const computedDetails = useMemo(() => {
        if (!details)
            return undefined;
        // If it's a UserDetailsConfig object (has 'fields' property)
        if (isUserDetailsConfig(details)) {
            return (item) => ({
                fields: buildDetailFields(item, details.fields),
                columns: details.columns,
                layout: details.layout,
                valueAlign: details.valueAlign
            });
        }
        // If it's a simple array of field names
        if (isUserDetailFieldArray(details)) {
            return (item) => {
                const props = {
                    fields: buildDetailFields(item, details),
                    layout: 'vertical',
                    columns: 1
                };
                console.log('UserInfoCard returning:', props);
                return props;
            };
        }
        // Otherwise, it's already InfoCardDetailsContent (function or ReactNode)
        return details;
    }, [details, user]);
    const infoCardData = effectiveUser ? transformedData : {};
    return (_jsx(InfoCard, { data: infoCardData, fields: {
            image: 'profileImageUrl',
            title: 'FullName',
            subtitle: 'Email',
            name: 'FullName'
        }, details: computedDetails, variant: variant, size: size, shape: shape, dropdownPosition: dropdownPosition, className: className, style: style }));
};
export default UserInfoCard;
